// Los modelos devuelven prosa con marcas de formato aunque el prompt no las
// pida, y no siempre en el mismo dialecto. En una misma planificacion aparecen
// las tres formas:
//
//   **negrita**  *cursiva*        markdown, la mas comun
//   <b>negrita</b> <i>cursiva</i> HTML, sobre todo en los titulos de slide
//   ++subrayado++                 markdown de "insertado" (markdown-it-ins)
//
// Las slides pintaban ese texto tal cual, asi que al profesor le aparecia
// `**huevos**` o `<b>plantas</b>` en pantalla.
//
// Convertir con `v-html` obliga a escapar primero: el texto viene de un modelo,
// o sea de una fuente no confiable. El orden es escapar -> convertir, nunca al
// reves, para que un `<script>` en la respuesta quede inerte. Lo unico que
// vuelve a ser HTML es la lista blanca de `TAGS_INLINE`, y siempre sin
// atributos: un `<b onclick=...>` no sobrevive al escape.

const ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (ch) => ESCAPES[ch]);
}

// Etiquetas que el modelo manda en vez de markdown, y a que se traducen.
// Cualquier otra queda escapada y se ve como texto, que es lo que corresponde.
const TAGS_INLINE = {
  b: "strong",
  strong: "strong",
  i: "em",
  em: "em",
  u: "u",
  ins: "u",
};

/**
 * Repone las etiquetas inline de la lista blanca despues del escape.
 * `&lt;b&gt;` vuelve a ser `<strong>`; `&lt;script&gt;` se queda escapado.
 */
function reponerTagsInline(html) {
  return html
    .replace(
      /&lt;(\/?)(b|strong|i|em|u|ins)&gt;/gi,
      (_, cierre, tag) => `<${cierre}${TAGS_INLINE[tag.toLowerCase()]}>`
    )
    .replace(/&lt;br\s*\/?&gt;/gi, "<br>");
}

/**
 * Envuelve `cuerpo` en `tag` dejando fuera los espacios que venian pegados a
 * los delimitadores.
 *
 * El modelo escribe `**oxígeno **y refugio` con el espacio DENTRO de la marca.
 * La version anterior exigia no-espacio antes del cierre, asi que ese caso no
 * calzaba con nada y los asteriscos quedaban crudos en pantalla. Ahora calza,
 * y el espacio se re-emite por fuera para no pegar las palabras
 * (`<strong>oxígeno</strong> y refugio`, no `<strong>oxígeno</strong>y`).
 */
function envolver(tag) {
  return (_, izq, cuerpo, der) => `${izq}<${tag}>${cuerpo}</${tag}>${der}`;
}

// Un cuerpo es al menos un caracter visible, y no puede empezar ni terminar en
// espacio: eso lo captura el grupo de afuera.
// El `??` del final es a proposito: el grupo opcional se prueba VACIO primero,
// asi el cierre que gana es el `**` mas cercano. Siendo goloso, "**a** y **b**"
// se leia como una sola negrita de "a** y".
const CUERPO = "(\\S(?:[\\s\\S]*?\\S)??)";
const NEGRITA = new RegExp(`\\*\\*([ \\t]*)${CUERPO}([ \\t]*)\\*\\*`, "g");
// Para `++` se exige ademas que la marca no venga pegada a una palabra, si no
// "C++ y C++" se leeria como un subrayado que abarca " y C".
const SUBRAYADO = new RegExp(
  `(^|[^\\w+])\\+\\+([ \\t]*)${CUERPO}([ \\t]*)\\+\\+(?![\\w+])`,
  "g"
);

/**
 * Escapa el texto y convierte el formato inline que mandan los modelos.
 * Soporta `**negrita**`, `*cursiva*`, `++subrayado++` y las etiquetas de
 * `TAGS_INLINE`. Los guiones bajos NO se interpretan (ver la nota al final:
 * chocan con los espacios para completar).
 *
 * @param {string} value texto crudo del modelo
 * @returns {string} HTML seguro, apto para v-html
 */
export function inlineMarkdown(value) {
  if (value === null || value === undefined) return "";

  let html = reponerTagsInline(escapeHtml(value));

  // Negrita antes que cursiva: si corriera al reves, `**x**` se comeria como
  // dos cursivas vacias alrededor de la palabra.
  html = html.replace(NEGRITA, envolver("strong"));

  // Cursiva: se exige no-espacio pegado al asterisco para no tocar vinetas
  // ("* item") ni multiplicaciones sueltas en los ejercicios de matematica.
  html = html.replace(/(^|[^*\w])\*(?=\S)([\s\S]*?\S)\*(?![*\w])/g, "$1<em>$2</em>");

  // `++texto++` es "insertado" en los dialectos de markdown que lo traen, y se
  // pinta subrayado. Se respeta ese significado en vez de convertirlo a
  // negrita: cambiar el nivel de enfasis que eligio el modelo seria inventar.
  html = html.replace(SUBRAYADO, (m, antes, izq, cuerpo, der) =>
    `${antes}${izq}<u>${cuerpo}</u>${der}`
  );

  // A proposito NO se soportan `__negrita__` ni `_cursiva_`.
  //
  // El modelo escribe plantillas para completar: "La planta es importante
  // porque ___. Por ejemplo, ___. Por eso debemos ___". Con la forma de guion
  // bajo activada, dos espacios en blanco separados se leen como apertura y
  // cierre de negrita y el texto del medio termina resaltado con su pildora de
  // color. En material educativo los ___ son mucho mas frecuentes que la
  // negrita con guiones bajos, que ademas los modelos casi no usan: mandan `**`.

  return html;
}

export default inlineMarkdown;
